import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import SubmitButton from './SubmitButton';

/**
 * Standard destructive-action confirmation dialog.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={deleteOpen}
 *     onClose={() => setDeleteOpen(false)}
 *     onConfirm={handleDelete}
 *     title="Delete journal entry?"
 *     message="This entry will be permanently deleted. You'll have a few seconds to undo."
 *     confirmLabel="Delete"
 *     destructive
 *     loading={deleting}
 *   />
 *
 * Props:
 *   open            — boolean
 *   onClose         — fires on cancel / backdrop / esc
 *   onConfirm       — fires on confirm click
 *   title           — heading
 *   message         — body text (string or React node)
 *   confirmLabel    — defaults to 'Confirm'
 *   cancelLabel     — defaults to 'Cancel'
 *   destructive     — uses error color on the confirm button
 *   loading         — disables + shows spinner on confirm while async work runs
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
}) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
  >
    <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>{title}</DialogTitle>
    {message && (
      <DialogContent sx={{ pt: '8px !important' }}>
        {typeof message === 'string'
          ? <DialogContentText>{message}</DialogContentText>
          : message}
      </DialogContent>
    )}
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      <Button
        onClick={onClose}
        disabled={loading}
        sx={{ color: 'text.secondary', textTransform: 'none' }}
      >
        {cancelLabel}
      </Button>
      <SubmitButton
        loading={loading}
        onClick={onConfirm}
        color={destructive ? 'error' : 'primary'}
        autoFocus
      >
        {confirmLabel}
      </SubmitButton>
    </DialogActions>
  </Dialog>
);

export default ConfirmDialog;
